// Modular guns (Tarkov-style builds). A gun is a receiver with slots; a part fits a slot when its type AND mount
// match, and parts bring their own slots (barrel -> muzzle, dust cover -> optic ...). A build maps slot paths to
// part ids, e.g. { "barrel": "v47_bar_std", "barrel/muzzle": "s762r" }. Builds are immutable: replace, never edit.
// Pure functions shared by server and client. Data: common/data/gunsmith.json. How to add guns: docs/WEAPONS.md.
import gunsmithJ from '../data/gunsmith.json';
import type { AmmoType, WeaponSpec } from './data';

export const MODULAR_IDS = ['v47', 'vs12', 'p11', 'k5', 's10'] as const;
export type ModularId = typeof MODULAR_IDS[number];
export type Build = Record<string, string>;
export interface GSlot { id: string; label: string; types: string[]; mounts: string[]; req?: boolean }
export type ModKey = 'ergo' | 'recoil' | 'acc' | 'vel' | 'noise' | 'reload' | 'rpm';
export interface GPart {
  name: string; maker: string; type: string; mount: string; price: number; mod?: Partial<Record<ModKey, number>>;
  weight?: number; len?: number; moa?: number; cap?: number; capAdd?: number; zoom?: number; fx?: Record<string, number>;
  slots?: GSlot[]; conflicts?: string[]; note?: string; draw?: any;
}
export interface GBase { damage: number; rpm: number; vel: number; range: number; recV: number; recH: number; ergo: number; weight: number; noise: number; reload: number; price: number; len: number; moa?: number }
export interface GPlatform {
  id: ModularId; name: string; maker: string; cls: string; based: string; caliber: string; modes: string[]; role: string; blurb: string;
  pellets?: number; baseCap?: number | null; slots: GSlot[]; defaults: Build; presets: { name: string; parts: Build }[];
  base: GBase; stockTarget?: Partial<GBase>; draw?: any;
}
export interface Mapping {
  spreadPerMoa: number; climbPerRecV: number; wobblePerRecH: number; recoverPerS: number; recoverDelay: number; bloomMaxDeg: number;
  readyBase: number; readyErgo: number; walkFreeKg: number; walkPerKg: number; walkMax: number;
  pxPerM: number; speedBase: number; speedPerMs: number; noisePx: number; lookPx: number; lookMax: number; driveByMaxCm: number;
}
export const GUNSMITH = gunsmithJ as unknown as {
  version: number; mapping: Mapping; makers: Record<string, { name: string }>; calibers: Record<string, { name: string; pool: AmmoType }>;
  types: Record<string, string>; platforms: GPlatform[]; parts: Record<string, GPart>;
};
export const M = GUNSMITH.mapping;
export const PARTS = GUNSMITH.parts;
export const GUNS = Object.fromEntries(GUNSMITH.platforms.map(p => [p.id, p])) as Record<ModularId, GPlatform>;
export const isModular = (w: unknown): w is ModularId => typeof w === 'string' && Object.prototype.hasOwnProperty.call(GUNS, w);
export const part = (id: string | undefined | null): GPart | null => (id && Object.prototype.hasOwnProperty.call(PARTS, id) ? PARTS[id] : null);
export const fits = (p: GPart, s: GSlot) => s.types.includes(p.type) && s.mounts.includes(p.mount);
export const gunName = (pl: GPlatform) => `${GUNSMITH.makers[pl.maker]?.name.split(' ')[0] ?? ''} ${pl.name}`.trim();
const DEG = Math.PI / 180;

export interface GNode { path: string; slot: GSlot; id: string | null; depth: number }
/** Walk the slot tree. Unreachable paths are ignored; a part that doesn't fit its slot is listed as invalid. */
export function resolve(pl: GPlatform, b: Build) {
  const nodes: GNode[] = [], used: string[] = [], missing: GNode[] = [], invalid: string[] = [];
  const walk = (slots: GSlot[], prefix: string, depth: number) => {
    for (const s of slots) {
      const path = prefix ? `${prefix}/${s.id}` : s.id;
      const p = part(b[path]); const ok = !!p && fits(p, s);
      if (b[path] && !ok) invalid.push(path);
      const n: GNode = { path, slot: s, id: ok ? b[path] : null, depth }; nodes.push(n);
      if (ok) { used.push(b[path]); walk(p!.slots ?? [], path, depth + 1); } else if (s.req) missing.push(n);
    }
  };
  walk(pl.slots, '', 0);
  const set = new Set(used); const conflicts: [string, string][] = [];
  for (const id of set) for (const c of PARTS[id].conflicts ?? []) if (set.has(c) && id < c) conflicts.push([id, c]);
  return { nodes, used, missing, invalid, conflicts, ok: !missing.length && !invalid.length && !conflicts.length };
}
/** Keep only reachable, fitting paths. */
export function prune(pl: GPlatform, b: Build): Build {
  const out: Build = {}; for (const n of resolve(pl, b).nodes) if (n.id) out[n.path] = n.id; return out;
}
/** Parts that fit a slot, cheapest first. */
export function options(s: GSlot) { return Object.keys(PARTS).filter(id => fits(PARTS[id], s)).sort((a, b) => PARTS[a].price - PARTS[b].price || (PARTS[a].name < PARTS[b].name ? -1 : 1)); }

/** Fitted parts others can see on a held gun, as `PlayerSnap.gp` bits: 1 optic, 2 suppressor, 4 a magazine that
 *  holds more than the platform's default one. Display only; 0 for the stock look. */
export const HELD_OPTIC = 1, HELD_SUPPRESSOR = 2, HELD_EXTMAG = 4;
export function heldPartBits(pl: GPlatform, b: Build): number {
  let bits = 0;
  const stockCap = part(pl.defaults[Object.keys(pl.defaults).find(k => PARTS[pl.defaults[k]]?.type === 'magazine') ?? ''])?.cap ?? 0;
  for (const id of resolve(pl, b).used) {
    const p = PARTS[id];
    if (p.type === 'optic') bits |= HELD_OPTIC;
    else if (p.type === 'suppressor') bits |= HELD_SUPPRESSOR;
    else if (p.type === 'magazine' && (p.cap ?? 0) > stockCap) bits |= HELD_EXTMAG;
  }
  return bits;
}

export interface GunStats {
  damage: number; pellets: number; rpm: number; vel: number; range: number; moa: number; recV: number; recH: number;
  ergo: number; weight: number; len: number; noise: number; cap: number; reload: number; zoom: number; price: number; ok: boolean; missing: string[];
}
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
/** Stat roll-up: ergo, weight, length, noise and price add flat; recoil, accuracy, velocity, reload and fire rate are summed percentages. */
export function stats(pl: GPlatform, b: Build): GunStats {
  const r = resolve(pl, b); const B = pl.base;
  const t: Record<ModKey, number> = { ergo: 0, recoil: 0, acc: 0, vel: 0, noise: 0, reload: 0, rpm: 0 };
  let weight = B.weight, len = B.len, price = B.price, moa = B.moa ?? 0, cap = pl.baseCap ?? 0, capAdd = 0, zoom = 1;
  for (const id of r.used) {
    const p = PARTS[id];
    for (const [k, v] of Object.entries(p.mod ?? {})) t[k as ModKey] += v as number;
    weight += p.weight ?? 0; len += p.len ?? 0; price += p.price;
    if (p.moa != null) moa = p.moa; if (p.cap != null) cap = p.cap; capAdd += p.capAdd ?? 0; if (p.zoom) zoom = Math.max(zoom, p.zoom);
  }
  const vf = 1 + t.vel / 100, rf = Math.max(0.1, 1 + t.recoil / 100);
  return {
    damage: B.damage * (1 + 0.5 * (vf - 1)), pellets: pl.pellets ?? 1, rpm: B.rpm * (1 + t.rpm / 100), vel: B.vel * vf, range: B.range * vf,
    moa: moa * Math.max(0.2, 1 + t.acc / 100), recV: B.recV * rf, recH: B.recH * rf, ergo: clamp(B.ergo + t.ergo, 0, 100),
    weight, len, noise: clamp(B.noise + t.noise, 5, 100), cap: cap + capAdd, reload: B.reload * Math.max(0.3, 1 + t.reload / 100),
    zoom, price, ok: r.ok, missing: r.missing.map(n => n.slot.label),
  };
}
export const stockBuild = (pl: GPlatform): Build => ({ ...pl.defaults });
export const bloomMax = M.bloomMaxDeg * DEG;

/** The build as a game weapon spec (game units: px, px/s, rad, s). */
export function gunSpec(pl: GPlatform, b: Build): WeaponSpec {
  const s = stats(pl, b);
  const walk = 1 - Math.min(M.walkMax, Math.max(0, s.weight - M.walkFreeKg) * M.walkPerKg);
  return {
    name: gunName(pl), kind: (pl.pellets ?? 1) > 1 ? 'pellet' : 'ballistic', damage: s.damage, cooldown: 60 / s.rpm,
    range: s.range * M.pxPerM, speed: M.speedBase + M.speedPerMs * s.vel, spread: s.moa * M.spreadPerMoa,
    ammo: GUNSMITH.calibers[pl.caliber].pool, mag: s.ok ? s.cap : 0, reload: s.reload, price: Math.round(s.price),
    semi: !pl.modes.includes('auto'), pellets: s.pellets, noise: s.noise, length: s.len,
    climb: s.recV * M.climbPerRecV * DEG, wobble: s.recH * M.wobblePerRecH * DEG, recover: M.recoverPerS * (0.5 + s.ergo / 100) * DEG,
    ready: M.readyBase - M.readyErgo * s.ergo / 100, walk, look: Math.min(M.lookMax, M.lookPx * (s.zoom - 1)),
    quiet: (resolveQuiet(pl, b)), ok: s.ok,
  };
}
function resolveQuiet(pl: GPlatform, b: Build) { return resolve(pl, b).used.some(id => PARTS[id].type === 'suppressor'); }

/** Data checks run by validateData(). */
export function validateGunsmith(): string[] {
  const e: string[] = [];
  const ids = GUNSMITH.platforms.map(p => p.id);
  if (ids.join() !== MODULAR_IDS.join()) e.push(`gunsmith platforms ${ids} must match MODULAR_IDS ${MODULAR_IDS}`);
  const served = new Set<string>();
  for (const [id, p] of Object.entries(PARTS)) {
    if (!(p.price >= 0) || !p.name || !p.type || !p.mount) e.push(`gunsmith part ${id} needs name, type, mount and price`);
    for (const c of p.conflicts ?? []) if (!PARTS[c]?.conflicts?.includes(id)) e.push(`gunsmith conflict ${id} -> ${c} is not mirrored`);
  }
  for (const pl of GUNSMITH.platforms) {
    if (!GUNSMITH.calibers[pl.caliber]) e.push(`gunsmith ${pl.id} caliber ${pl.caliber} unknown`);
    if (!GUNSMITH.makers[pl.maker]) e.push(`gunsmith ${pl.id} maker unknown`);
    const builds = [['defaults', pl.defaults], ...pl.presets.map(x => [`preset ${x.name}`, x.parts])] as [string, Build][];
    for (const [n, b] of builds) {
      const r = resolve(pl, b);
      if (!r.ok) e.push(`gunsmith ${pl.id} ${n}: missing ${r.missing.map(m => m.path)} invalid ${r.invalid} conflicts ${r.conflicts.map(c => c.join('+'))}`);
      if (Object.keys(b).length !== r.used.length) e.push(`gunsmith ${pl.id} ${n} has unreachable paths`);
    }
    // every slot reachable on this gun has at least one part
    const slots = [...pl.slots];
    for (let i = 0; i < slots.length; i++) {
      const opts = options(slots[i]); if (!opts.length) e.push(`gunsmith ${pl.id} slot ${slots[i].id} has no parts`);
      for (const id of opts) if (!served.has(`${pl.id}:${id}`)) { served.add(`${pl.id}:${id}`); slots.push(...(PARTS[id].slots ?? [])); }
    }
  }
  for (const id of Object.keys(PARTS)) if (!GUNSMITH.platforms.some(pl => served.has(`${pl.id}:${id}`))) e.push(`gunsmith part ${id} fits no gun`);
  return e;
}
